import React, { useEffect } from "react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import { BRAND, VALUES, STATS } from "@/lib/brand";
import { ShieldCheck, Compass, HeartHandshake, Target } from "lucide-react";

const CONSULTING_IMG = "https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbnN1bHRhbnQlMjBoYW5kc2hha2V8ZW58MHx8fHwxNzgyMzA0MDc2fDA&ixlib=rb-4.1.0&q=85";

export default function About() {
  useEffect(() => { document.title = `About — ${BRAND.name}`; }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />

      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <div className="eyebrow">About the firm</div>
          <h1 className="font-heading font-bold text-4xl md:text-6xl leading-[1.05] mt-4">
            Two decades<br />
            of standing with <span className="text-brand-gradient">policyholders.</span>
          </h1>
          <p className="mt-6 text-slate-600 leading-relaxed text-lg max-w-xl">
            Accurate Claims Services was founded to fix one persistent problem: policyholders losing fair settlements
            simply because they didn't have the right paperwork or the right advocate. We are an independent claim
            advisory — never an insurer, never a commission-earning intermediary.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
            {STATS.slice(0,3).map((s) => (
              <div key={s.label}>
                <div className="font-heading font-bold text-3xl text-navy">{s.value}</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-5">
          <div className="rounded-[28px] overflow-hidden brand-shadow-lg aspect-[4/5]">
            <img src={CONSULTING_IMG} alt="Insurance claim consulting" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="bg-white border-y border-slate-200/70">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 grid md:grid-cols-2 gap-8">
          <div className="brand-card p-10">
            <div className="w-12 h-12 rounded-xl bg-emerald-brand/10 text-emerald-brand flex items-center justify-center">
              <Target className="w-5 h-5" />
            </div>
            <h3 className="font-heading font-bold text-2xl mt-5">Our Mission</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              To make sure every policyholder gets a fair outcome on every claim — through honest advice,
              meticulous documentation and persistent representation.
            </p>
          </div>
          <div className="brand-card p-10">
            <div className="w-12 h-12 rounded-xl bg-gold/15 text-gold flex items-center justify-center">
              <Compass className="w-5 h-5" />
            </div>
            <h3 className="font-heading font-bold text-2xl mt-5">Our Vision</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              To be India's most trusted independent claim advisory — known for accuracy, transparency
              and a relentless focus on the policyholder.
            </p>
          </div>
        </div>
      </section>

      {/* Core values */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="eyebrow justify-center inline-flex">Core values</div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl mt-3">{BRAND.values}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          {VALUES.map((v) => (
            <div key={v} className="brand-card p-5 text-center">
              <HeartHandshake className="w-5 h-5 mx-auto text-gold" />
              <div className="font-heading font-semibold text-sm mt-3">{v}</div>
            </div>
          ))}
        </div>
      </section>

      <SiteFooter />
      <WhatsAppFAB />
    </div>
  );
}
